/**
 * Manufacturing feature switches the screens read (`GET
 * /manufacturing-work-orders/features`): whether the saws print stack labels
 * and the floor scans them (GLOBAL setting `manufacturing.saw_labels_enabled`,
 * default off — David 2026-10-20: the saw label printers are not in use).
 * Off: saws need no printer, Start prints nothing, Reprint is hidden, the
 * operator records what the saw made with **Record parts**, and a saw line is
 * finished with its **Finish line** (manufacturing v3). One request per
 * session (5 min stale), shared by every manufacturing screen; while loading
 * (or when the API predates it) the screens assume OFF.
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../client';
import { queryKeys } from '../queryKeys';

export interface ManufacturingFeatures {
  sawLabelsEnabled: boolean;
  /** End-of-line count (2026-10-30): the Count parts tablet page is on. */
  endCountEnabled: boolean;
  /** Manufacturing v3: the completion percentage new work orders start with. */
  completionThresholdPct: number;
  /** The percentages a manager may pick. */
  completionThresholdOptions: number[];
  /** `kitchens.cabinet_serials` is ON: cabinet labels print at the assembly station, numbered. */
  cabinetSerialsOn: boolean;
  /** `kitchens.print_all_labels` is ON: Assembly's Print all is for everyone who prints label 1, not only managers. */
  kitchenPrintAllLabelsOn: boolean;
  /** `kitchens.end_count` is ON: the kitchen Count parts tablet (Kitchens → Production) is on. */
  kitchenEndCountOn?: boolean;
  /** `kitchens.order_shipping` is ON: kitchen shipping tracks orders, not trucks (the screens say "shipment"). */
  kitchenOrderShippingOn: boolean;
  /** Buffer parts B-1: GLOBAL `manufacturing.buffer_parts_enabled` — closet work orders use the buffer shelf. */
  bufferPartsOn?: boolean;
  /** Buffer parts B-1: `kitchens.buffer_parts` is ON — kitchen work orders use the buffer shelf. */
  kitchenBufferPartsOn?: boolean;
}

/** The percentages a manager may pick (the API's completion-threshold-setting). */
export const THRESHOLD_OPTIONS = [100, 95, 85, 75, 50] as const;

export const DEFAULT_MANUFACTURING_FEATURES: ManufacturingFeatures = {
  sawLabelsEnabled: false,
  endCountEnabled: false,
  completionThresholdPct: 85,
  completionThresholdOptions: [...THRESHOLD_OPTIONS],
  cabinetSerialsOn: false,
  kitchenPrintAllLabelsOn: false,
  kitchenEndCountOn: false,
  kitchenOrderShippingOn: false,
  bufferPartsOn: false,
  kitchenBufferPartsOn: false,
};

export function useManufacturingFeatures() {
  return useQuery({
    queryKey: queryKeys.manufacturingWorkOrders.features,
    queryFn: async () => {
      const { data } = await apiClient.get<ManufacturingFeatures>(
        '/api/manufacturing-work-orders/features',
      );
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** True when saws print stack labels and the floor scans them. */
export function useSawLabelsEnabled(): boolean {
  const { data } = useManufacturingFeatures();
  return (
    data?.sawLabelsEnabled ?? DEFAULT_MANUFACTURING_FEATURES.sawLabelsEnabled
  );
}

/**
 * Buffer parts B-2: GLOBAL `manufacturing.buffer_parts_enabled` — closet work
 * orders use the buffer shelf (Add pieces, To shelve, In buffer / Expected /
 * Free on the Buffer tab, "put them on the buffer shelf"). False while loading
 * or on an API that predates it: the Buffer tab then reads as before.
 */
export function useBufferPartsOn(): boolean {
  const { data } = useManufacturingFeatures();
  return data?.bufferPartsOn ?? false;
}

/** True when the end-of-line Count parts page is on (GLOBAL `manufacturing.end_count_enabled`). */
export function useEndCountEnabled(): boolean {
  const { data } = useManufacturingFeatures();
  return (
    data?.endCountEnabled ?? DEFAULT_MANUFACTURING_FEATURES.endCountEnabled
  );
}

/**
 * `kitchens.end_count`: true while the kitchen Count parts page is on (its
 * own switch — the closet `endCountEnabled` never turns it on). False while
 * loading or on an API that predates it.
 */
export function useKitchenEndCountOn(): boolean {
  const { data } = useManufacturingFeatures();
  return data?.kitchenEndCountOn ?? false;
}

/**
 * `kitchens.cabinet_serials`: true while ON — label 1 prints at the assembly
 * station when a cabinet is finished, with the cabinet's own number, so the
 * older unnumbered label buttons (a kitchen project's Print labels, a kitchen
 * work order's Print cabinet labels) are hidden; the API refuses them too
 * (409 LABELS_PRINT_AT_ASSEMBLY). `undefined` while the features are loading
 * (or could not be read): not known yet, so those buttons show only on a
 * definite `false` — none appears and then 409s. An API that predates the
 * field → false (it has no such switch).
 */
export function useCabinetSerialsOn(): boolean | undefined {
  const { data } = useManufacturingFeatures();
  if (!data) return undefined;
  return (
    data.cabinetSerialsOn ?? DEFAULT_MANUFACTURING_FEATURES.cabinetSerialsOn
  );
}

/**
 * `kitchens.print_all_labels` (plan P-8, D-5): true while ON — Assembly's
 * Print all is offered to everyone who may print a cabinet's label 1; OFF
 * (and while loading, or on an API that predates it) only managers get it,
 * as the API allows more than one label per press only to them (Q8).
 */
export function useKitchenPrintAllLabelsOn(): boolean {
  const { data } = useManufacturingFeatures();
  return (
    data?.kitchenPrintAllLabelsOn ??
    DEFAULT_MANUFACTURING_FEATURES.kitchenPrintAllLabelsOn
  );
}

/**
 * `kitchens.order_shipping` (plan P-10, D-14): true while ON — the dock scans
 * cabinets on their ORDER (no trucks) and the kitchen screens say "shipment"
 * where they said "truck". `undefined` while the features are first loading:
 * the dock waits rather than offer Start truck for a moment (the API refuses
 * it while ON). Could not be read, or an API that predates the field → false
 * (today's trucks and words).
 */
export function useKitchenOrderShippingOn(): boolean | undefined {
  const { data, isLoading } = useManufacturingFeatures();
  if (!data && isLoading) return undefined;
  return (
    data?.kitchenOrderShippingOn ??
    DEFAULT_MANUFACTURING_FEATURES.kitchenOrderShippingOn
  );
}

/** "Start & print" / "Start" — the one-line verb of a saw line's start. */
export const startVerb = (labelsOn: boolean) =>
  labelsOn ? 'Start & print' : 'Start';

/** "Start all & print" / "Start all". */
export const startAllVerb = (labelsOn: boolean) =>
  labelsOn ? 'Start all & print' : 'Start all';

/** The completion percentage a new work order starts with (85 while loading or on an older API). */
export function useDefaultCompletionThreshold(): number {
  const { data } = useManufacturingFeatures();
  return (
    data?.completionThresholdPct ??
    DEFAULT_MANUFACTURING_FEATURES.completionThresholdPct
  );
}
